import React, { useState, useEffect } from 'react';
import { Search, Plus, Users, PhoneCall, TrendingUp, Calendar, ChevronDown, MoreVertical, UserPlus, X, Loader2, Briefcase, DollarSign, Code, UserCheck, ChevronRight, Edit3, Trash2, Check, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';

// Use runtime window.location.origin for API URL
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

// Icon mapping for department icons
const iconMap = {
  Briefcase,
  DollarSign,
  TrendingUp,
  Users,
  Code,
  UserCheck
};

// Icon options for department creation
const iconOptions = [
  { icon: Briefcase, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  { icon: DollarSign, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  { icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { icon: Users, color: 'text-green-500', bg: 'bg-green-500/10' },
  { icon: Code, color: 'text-pink-500', bg: 'bg-pink-500/10' },
  { icon: UserCheck, color: 'text-orange-500', bg: 'bg-orange-500/10' }
];

const RealEstateEmployees = () => {
  // Main tab state
  const [activeMainTab, setActiveMainTab] = useState('employees');
  
  // Get user from auth context
  const { user } = useAuth();
  const userId = user?.user_id || user?.email || '';
  
  const [activePerformanceTab, setActivePerformanceTab] = useState('Top Performances');
  const [activeDirectoryTab, setActiveDirectoryTab] = useState('Employee List');
  const [searchQuery, setSearchQuery] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    department: '',
    phone: '',
    type: 'Full-Time'
  });

  // Department states
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showAddDeptEmployeeModal, setShowAddDeptEmployeeModal] = useState(false);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [editingDepartmentName, setEditingDepartmentName] = useState('');
  
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10'
  });
  
  const [newDeptEmployee, setNewDeptEmployee] = useState({
    name: '',
    role: '',
    phone: '',
    avatar: ''
  });

  const [editEmployee, setEditEmployee] = useState({
    name: '',
    role: '',
    phone: '',
    avatar: ''
  });

  // Fetch employees from backend
  const fetchEmployees = async () => {
    const API_URL = getApiUrl();
    try {
      // Counterfind tenant gets a user_id-scoped list so they see only the
      // seeded mockup roster. Other accounts hit the legacy unscoped endpoint.
      const userEmail = (user?.email || '').toLowerCase();
      const isCounterfind = userEmail === 'counterfind@operatr.com';
      const qs = isCounterfind && userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      const response = await fetch(`${API_URL}/api/employees${qs}`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments from backend
  const fetchDepartments = async () => {
    if (!userId) {
      setDepartmentsLoading(false);
      return;
    }
    
    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        const deptWithIcons = data.map(dept => ({
          ...dept,
          icon: iconMap[dept.iconName] || Users
        }));
        setDepartments(deptWithIcons);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [userId]);

  // Add new employee
  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.role) {
      toast.error('Name and role are required');
      return;
    }

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEmployee)
      });

      if (response.ok) {
        const employee = await response.json();
        setEmployees(prev => [...prev, employee]);
        setShowAddModal(false);
        setNewEmployee({ name: '', role: '', department: '', phone: '', type: 'Full-Time' });
        toast.success('Employee added successfully');
      } else {
        toast.error('Failed to add employee');
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error('Failed to add employee');
    }
  };

  // Department functions
  const handleAddDepartment = async () => {
    if (!newDepartment.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDepartment.name,
          iconName: 'Briefcase',
          iconColor: newDepartment.iconColor,
          iconBg: newDepartment.iconBg,
          user_id: userId
        })
      });

      if (response.ok) {
        const dept = await response.json();
        setDepartments(prev => [...prev, { ...dept, icon: Briefcase, members: [] }]);
        setShowAddDepartmentModal(false);
        setNewDepartment({ name: '', iconColor: 'text-purple-500', iconBg: 'bg-purple-500/10' });
        toast.success('Department created successfully');
      }
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error('Failed to create department');
    }
  };

  const deleteDepartment = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDepartments(prev => prev.filter(d => d.id !== departmentId));
        toast.success('Department deleted');
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('Failed to delete department');
    }
  };

  const startEditingDepartmentName = (dept) => {
    setEditingDepartmentId(dept.id);
    setEditingDepartmentName(dept.name);
  };

  const saveDepartmentName = async (departmentId) => {
    if (!editingDepartmentName.trim()) {
      setEditingDepartmentId(null);
      return;
    }

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingDepartmentName })
      });

      if (response.ok) {
        setDepartments(departments.map(dept => 
          dept.id === departmentId ? { ...dept, name: editingDepartmentName } : dept
        ));
        toast.success('Department name updated');
      }
    } catch (error) {
      console.error('Error updating department:', error);
    }
    
    setEditingDepartmentId(null);
  };

  const openAddEmployeeModal = (departmentId) => {
    setSelectedDepartmentId(departmentId);
    setShowAddDeptEmployeeModal(true);
  };

  const handleAddDeptEmployee = async () => {
    if (!newDeptEmployee.name.trim() || !newDeptEmployee.role.trim()) {
      toast.error('Name and role are required');
      return;
    }

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${selectedDepartmentId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDeptEmployee.name,
          role: newDeptEmployee.role,
          phone: newDeptEmployee.phone || 'N/A',
          avatar: newDeptEmployee.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(newDeptEmployee.name)}&background=random`
        })
      });

      if (response.ok) {
        const member = await response.json();
        setDepartments(departments.map(dept => 
          dept.id === selectedDepartmentId 
            ? { ...dept, members: [...dept.members, member], memberCount: dept.memberCount + 1 }
            : dept
        ));
        setShowAddDeptEmployeeModal(false);
        setNewDeptEmployee({ name: '', role: '', phone: '', avatar: '' });
        toast.success('Employee added to department');
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error('Failed to add employee');
    }
  };

  const handleDeleteMember = async (departmentId, memberId) => {
    if (!window.confirm('Remove this member from the department?')) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}/members/${memberId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDepartments(departments.map(dept => 
          dept.id === departmentId 
            ? { ...dept, members: dept.members.filter(m => m.id !== memberId), memberCount: dept.memberCount - 1 }
            : dept
        ));
        toast.success('Member removed');
      }
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const handleUpdateDeptEmployee = async () => {
    if (!editingMember || !editEmployee.name || !editEmployee.role) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${editingMember.departmentId}/members/${editingMember.member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editEmployee)
      });

      if (response.ok) {
        const updated = await response.json();
        setDepartments(departments.map(dept => {
          if (dept.id === editingMember.departmentId) {
            return {
              ...dept,
              members: dept.members.map(m => 
                m.id === editingMember.member.id ? { ...m, ...updated } : m
              )
            };
          }
          return dept;
        }));
        toast.success('Employee updated');
        setShowEditEmployeeModal(false);
        setEditingMember(null);
        setEditEmployee({ name: '', role: '', phone: '', avatar: '' });
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee');
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.role?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats from real data
  const totalEmployees = employees.length;
  const topPerformers = [...employees]
    .filter(e => e.productivity)
    .sort((a, b) => (b.productivity || 0) - (a.productivity || 0))
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6">
      {/* Header with Tabs */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-4xl font-bold mb-1">Team Management</h1>
        <p className="text-gray-400 text-xs md:text-sm">Manage your workforce, departments and track performance</p>
        
        {/* Main Tabs */}
        <div className="flex gap-2 mt-4 border-b border-gray-700 pb-0">
          <button
            onClick={() => setActiveMainTab('employees')}
            data-testid="employees-tab"
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-[2px] ${
              activeMainTab === 'employees'
                ? 'text-blue-400 border-blue-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users size={16} />
              Employees
            </div>
          </button>
          <button
            onClick={() => setActiveMainTab('departments')}
            data-testid="departments-tab"
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-[2px] ${
              activeMainTab === 'departments'
                ? 'text-purple-400 border-purple-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Building2 size={16} />
              Departments
            </div>
          </button>
        </div>
      </div>

      {/* Employees Tab Content */}
      {activeMainTab === 'employees' && (
        <>
        {/* Section Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Employee Directory</h2>
        </div>

      {/* Top Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-6">
        {/* Total Employees Card */}
        <div className="bg-blue-600 rounded-lg p-4 md:p-6 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <Users size={24} className="text-white md:hidden" />
              <Users size={32} className="text-white hidden md:block" />
              <button className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-white/80 hover:text-white">
                All Time <ChevronDown size={14} />
              </button>
            </div>
            <p className="text-white/80 text-xs md:text-sm mb-1 md:mb-2">Total Employees</p>
            <p className="text-3xl md:text-5xl font-bold mb-1 md:mb-2">{totalEmployees}</p>
            <p className="text-white/70 text-[10px] md:text-sm">
              {totalEmployees === 0 ? 'Add your first employee' : 'Active employees'}
            </p>
          </div>
        </div>

        {/* Quick Actions Card */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-4 md:p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <UserPlus size={20} className="text-blue-400 md:hidden" />
            <UserPlus size={24} className="text-blue-400 hidden md:block" />
            <h3 className="text-sm md:text-lg font-semibold">Quick Actions</h3>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
          >
            <Plus size={18} />
            Add Employee
          </button>
        </div>

        {/* Top Performers Card */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-4 md:p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300 lg:col-span-2">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <TrendingUp size={20} className="text-green-400 md:hidden" />
            <TrendingUp size={24} className="text-green-400 hidden md:block" />
            <h3 className="text-sm md:text-lg font-semibold">Top Performers</h3>
          </div>
          {topPerformers.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {topPerformers.map((emp, index) => (
                <div key={emp.id || index} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                    {emp.name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{emp.name}</p>
                    <p className="text-xs text-gray-400 truncate">{emp.role}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No employees yet</p>
          )}
        </div>
      </div>

      {/* Employee Directory */}
      <div className="bg-black border-2 border-gray-400 rounded-lg p-4 md:p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg md:text-xl font-semibold">Employee Directory</h2>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 md:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-800 text-white text-sm px-9 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded-lg transition-colors whitespace-nowrap"
            >
              <Plus size={16} />
              <span className="hidden md:inline">Add Employee</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-blue-500" size={32} />
          </div>
        ) : filteredEmployees.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-xs md:text-sm border-b border-gray-700">
                  <th className="pb-3 font-medium">Employee</th>
                  <th className="pb-3 font-medium hidden md:table-cell">Department</th>
                  <th className="pb-3 font-medium hidden lg:table-cell">Phone</th>
                  <th className="pb-3 font-medium hidden lg:table-cell">Type</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                          {employee.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{employee.name}</p>
                          <p className="text-xs text-gray-400">{employee.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 hidden md:table-cell text-sm text-gray-300">{employee.department || '-'}</td>
                    <td className="py-3 hidden lg:table-cell text-sm text-gray-300">{employee.phone || '-'}</td>
                    <td className="py-3 hidden lg:table-cell">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        employee.type === 'Full-Time' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {employee.type || 'Full-Time'}
                      </span>
                    </td>
                    <td className="py-3">
                      <button className="p-1 hover:bg-gray-700 rounded">
                        <MoreVertical size={16} className="text-gray-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No employees yet</h3>
            <p className="text-gray-500 text-sm mb-4">Add your first employee to get started</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Employee
            </button>
          </div>
        )}
      </div>
      </>
      )}

      {/* Departments Tab Content */}
      {activeMainTab === 'departments' && (
        <>
          {/* Departments Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold">Department Management</h2>
              <p className="text-gray-400 text-xs md:text-sm">Organize your team by departments</p>
            </div>
            <button 
              onClick={() => setShowAddDepartmentModal(true)}
              data-testid="add-department-btn"
              className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-all text-sm md:text-base"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Add Department</span>
              <span className="sm:hidden">Add Dept</span>
            </button>
          </div>

          {/* Departments Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search departments or members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gray-600 text-sm"
            />
          </div>

          {/* Departments Loading/Empty State */}
          {departmentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-purple-500" size={32} />
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-600 rounded-lg">
              <Building2 size={48} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400 mb-2">No departments yet</p>
              <button 
                onClick={() => setShowAddDepartmentModal(true)}
                className="text-purple-400 hover:text-purple-300"
              >
                Create your first department →
              </button>
            </div>
          ) : (
            /* Departments Grid */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {filteredDepartments.map((department) => (
                <div
                  key={department.id}
                  className="bg-black border-2 border-gray-400 rounded-lg p-4 md:p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300"
                >
                  {/* Department Header */}
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                      <div className={`${department.iconBg} p-2 md:p-3 rounded-lg flex-shrink-0`}>
                        <department.icon className={department.iconColor} size={20} />
                      </div>
                      <div className="min-w-0 flex-1">
                        {editingDepartmentId === department.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingDepartmentName}
                              onChange={(e) => setEditingDepartmentName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveDepartmentName(department.id);
                                if (e.key === 'Escape') setEditingDepartmentId(null);
                              }}
                              className="bg-gray-800 border border-purple-500 rounded px-2 py-1 text-base md:text-xl font-bold w-full max-w-[200px] focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                            />
                            <button
                              onClick={() => saveDepartmentName(department.id)}
                              className="p-1.5 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                              title="Save"
                            >
                              <Check size={14} className="text-white" />
                            </button>
                            <button
                              onClick={() => setEditingDepartmentId(null)}
                              className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X size={14} className="text-white" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group/title">
                            <h2 className="text-base md:text-xl font-bold truncate">{department.name}</h2>
                            <button
                              onClick={() => startEditingDepartmentName(department)}
                              className="p-1 hover:bg-gray-800 rounded opacity-0 group-hover/title:opacity-100 transition-all"
                              title="Edit department name"
                            >
                              <Edit3 size={14} className="text-gray-400 hover:text-white" />
                            </button>
                          </div>
                        )}
                        <p className="text-gray-400 text-xs md:text-sm">{department.memberCount} members</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                      <button 
                        onClick={() => {
                          setSelectedDepartmentId(department.id);
                          setShowAddDeptEmployeeModal(true);
                        }}
                        className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-xs md:text-sm font-medium transition-all"
                      >
                        <UserPlus size={12} />
                        <span className="hidden sm:inline">Add</span>
                      </button>
                    </div>
                  </div>

                  {/* Members List */}
                  <div className="space-y-2 md:space-y-3">
                    {department.members?.length === 0 ? (
                      <div className="text-center py-6 md:py-8 border border-dashed border-gray-600 rounded-lg">
                        <Users size={28} className="mx-auto text-gray-600 mb-2" />
                        <p className="text-gray-400 text-xs md:text-sm mb-2">No members yet</p>
                        <button 
                          onClick={() => {
                            setSelectedDepartmentId(department.id);
                            setShowAddDeptEmployeeModal(true);
                          }}
                          className="text-purple-400 hover:text-purple-300 text-xs md:text-sm"
                        >
                          Add the first employee →
                        </button>
                      </div>
                    ) : (
                      department.members?.slice(0, 4).map((member) => (
                        <div
                          key={member.id}
                          className="bg-black border border-gray-400 rounded-lg p-3 md:p-4 flex items-center justify-between hover:border-white hover:shadow-[0_0_15px_rgba(255,255,255,0.3)] transition-all duration-300 group"
                        >
                          <div className="flex items-center gap-2 md:gap-3 min-w-0">
                            <img
                              src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=random`}
                              alt={member.name}
                              className="w-10 md:w-12 h-10 md:h-12 rounded-full object-cover flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <h3 className="font-semibold text-sm md:text-base truncate">{member.name}</h3>
                              <p className="text-gray-400 text-xs md:text-sm truncate">{member.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                            <span className="text-gray-400 text-xs md:text-sm hidden sm:inline mr-2">{member.phone}</span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingMember({ departmentId: department.id, member });
                                  setEditEmployee({
                                    name: member.name,
                                    role: member.role,
                                    phone: member.phone || '',
                                    avatar: member.avatar || ''
                                  });
                                  setShowEditEmployeeModal(true);
                                }}
                                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
                                title="Edit member"
                              >
                                <Edit3 size={14} className="text-gray-400 hover:text-white" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMember(department.id, member.id);
                                }}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Remove member"
                              >
                                <Trash2 size={14} className="text-red-400 hover:text-red-300" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Add New Employee</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Role *</label>
                <input
                  type="text"
                  value={newEmployee.role}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Software Engineer"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Department</label>
                <input
                  type="text"
                  value={newEmployee.department}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, department: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Engineering"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Employment Type</label>
                <select
                  value={newEmployee.type}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Full-Time">Full-Time</option>
                  <option value="Part-Time">Part-Time</option>
                  <option value="Contract">Contract</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEmployee}
                className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Add Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {showAddDepartmentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Add New Department</h2>
              <button onClick={() => setShowAddDepartmentModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Department Name *</label>
                <input
                  type="text"
                  value={newDepartment.name}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Engineering"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Icon Color</label>
                <div className="flex gap-2 flex-wrap">
                  {iconOptions.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setNewDepartment(prev => ({ ...prev, iconColor: opt.color, iconBg: opt.bg }))}
                      className={`p-3 rounded-lg ${opt.bg} ${newDepartment.iconColor === opt.color ? 'ring-2 ring-white' : ''}`}
                    >
                      <opt.icon className={opt.color} size={20} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddDepartmentModal(false)}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDepartment}
                className="flex-1 py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Add Department
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Dept Employee Modal */}
      {showAddDeptEmployeeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Add Employee to Department</h2>
              <button onClick={() => setShowAddDeptEmployeeModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={newDeptEmployee.name}
                  onChange={(e) => setNewDeptEmployee(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Role *</label>
                <input
                  type="text"
                  value={newDeptEmployee.role}
                  onChange={(e) => setNewDeptEmployee(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Software Engineer"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={newDeptEmployee.phone}
                  onChange={(e) => setNewDeptEmployee(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddDeptEmployeeModal(false)}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDeptEmployee}
                className="flex-1 py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Add Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditEmployeeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Edit Employee</h2>
              <button onClick={() => setShowEditEmployeeModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={editEmployee.name}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Role *</label>
                <input
                  type="text"
                  value={editEmployee.role}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={editEmployee.phone}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditEmployeeModal(false)}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateDeptEmployee}
                className="flex-1 py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateEmployees;
